import { QueryDefinition, SupportedDataType } from '../../core';
import { DataQueryConfig, PluginImplementation, PluginType, VizFrameworkConfigArgs } from '../../plugin-system';

export const toQueryProviderFormat = (queries: QueryDefinition[]): DataQueryConfig[] =>
  queries.map((query) => {
    const { plugin, disabled, alias } = query.spec;
    return { definition: plugin, disabled, alias };
  });

export interface GetQueryDefinitionsFromDataType<T extends PluginType> {
  getPlugin: (pluginType: T, kind: string) => Promise<PluginImplementation<T>>;
  dataType: SupportedDataType;
  defaultDataQueryKind: string;
  queries?: QueryDefinition[];
  config?: VizFrameworkConfigArgs;
}

/**
 * This function processes each query to generate an updated spec based on the provided data type.
 * If no query is provided, it will generate a query definition using the default data query kind.
 */
export const getQueryDefinitionsFromDataType = async (args: GetQueryDefinitionsFromDataType<'DataQuery'>) => {
  const { getPlugin, dataType, defaultDataQueryKind, queries, config } = args;
  // Update query definitions based on the next panel kind
  const queryDefinitions: QueryDefinition[] = [];
  if (queries?.length) {
    for (let i = 0; i < queries.length; i++) {
      const queryDefinition = queries[i];
      if (!queryDefinition) continue;
      const { kind: queryKind, spec: querySpec } = queryDefinition.spec.plugin;
      const queryPlugin = await getPlugin('DataQuery', queryKind);
      const newSpec = queryPlugin.createInitialOptions({ dataType, prevSpec: querySpec, config });
      // Preserve disabled, alias, and id fields from the original query definition
      const { disabled, alias, id } = queryDefinition.spec;
      queryDefinitions.push({
        kind: 'DataQuery',
        spec: {
          plugin: {
            kind: queryKind,
            spec: newSpec,
          },
          ...(disabled !== undefined && { disabled }),
          ...(alias !== undefined && { alias }),
          ...(id !== undefined && { id }),
        },
      } as QueryDefinition);
    }
  } else {
    const queryPlugin = await getPlugin('DataQuery', defaultDataQueryKind);
    const newSpec = queryPlugin.createInitialOptions({ dataType, config });
    queryDefinitions.push({
      kind: 'DataQuery',
      spec: {
        plugin: {
          kind: defaultDataQueryKind,
          spec: newSpec,
        },
      },
    } as QueryDefinition);
  }

  return queryDefinitions;
};
